import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  PENDING_THREAD_KEY,
  useMessagesStore,
} from "../../../../store/messages.store";
import { useRef, useState } from "react";
import { useAuthStore } from "../../../../store/auth.store";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ClearIcon from "@mui/icons-material/Clear";
import MicIcon from "@mui/icons-material/Mic";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  formatMimetypeToFriendlyName,
  getAllowedFiles,
  getFileType,
} from "@shared/utils/mimetype";
import { compressImage } from "@shared/utils/compress-image";
import {
  MessageBarForm,
  MessageBarContainer,
  AttachButton,
  DiscardButton,
  MicButton,
  UtilityBar,
  SendButton,
  FileName,
} from "./styles";
import { RecordingIndicator } from "./RecordingIndicator";
import { MessageBarTextarea } from "./MessageBarTextarea";
import { useAutosizeTextarea } from "./useAutosizeTextarea";
import { useUIStore } from "../../../../store/ui.store";
import { useSetElementOnStore } from "../../../../shared/hooks/useSetElementOnStore";
import { useVoiceTranscription } from "@shared/hooks/useVoiceTranscription";
import { formatMessage, getNewTempUserMessage } from "./helpers";
import { CircularProgress, Tooltip, Typography } from "@mui/material";
import { ThreadAutoInteractionConfig } from "@features/Chat/chat-thread-auto-interaction-config";
import { classes } from "@shared/utils/classes";
import { IconButton, useBreakpoint } from "@avenue-8/sidekick-ui";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { useFeatureFlag } from "@shared/services/posthog/use-feature-flag";
import { toast } from "@shared/services/toast/toast";
import type { ToastPosition } from "react-toastify";

const getComposerText = () => useUIStore.getState().messageInputTextValue;

function getPlaceholder({
  hasMessages,
  hasMlsAccessModule,
  isMobile,
}: {
  hasMessages: boolean;
  hasMlsAccessModule: boolean;
  isMobile: boolean;
}) {
  if (hasMessages) {
    return isMobile ? "Reply to Sidekick" : "Reply to Sidekick to Refine Results";
  }

  if (!hasMlsAccessModule) {
    return "Ask who to follow up with next...";
  }

  return "Tell Sidekick to search a listing, write marketing copy, create a CMA...";
}

interface MessageBarProps {
  currentThread?: string;
  onSendMessage: (params: { message: string }) => Promise<{ threadId: string }>;
  onAttachFile: (file: File) => Promise<string>;
  numberOfMessages: number;
  isSendingMessage: boolean;
  isReceivingMessageResponse: boolean;
  isUploadingFile: boolean;
  disabled?: boolean;
  tooltip?: string;
  threadAutoInteractionConfig?: ThreadAutoInteractionConfig | null;
  hasMoreAutoInteractions: boolean;
  nextMessageIndex: number;
  handleFinishAutoTyping: () => void;
  isOverlayHidden: boolean;
  isSubmitDisabled?: boolean;
}

export const MessageBar = (props: MessageBarProps) => {
  const {
    currentThread,
    onSendMessage,
    onAttachFile,
    numberOfMessages,
    isSendingMessage,
    isReceivingMessageResponse,
    isUploadingFile,
    disabled,
    tooltip,
    threadAutoInteractionConfig,
    isOverlayHidden,
    hasMoreAutoInteractions,
    nextMessageIndex,
    handleFinishAutoTyping,
    isSubmitDisabled,
  } = props;
  const addMessage = useMessagesStore((state) => state.upsertMessage);
  const setMessages = useMessagesStore((state) => state.setMessages);
  const user = useAuthStore((state) => state.user);
  const hasMlsAccessModule = useHasModule(sidekickModuleEnum.MLS_ACCESS);
  const isVoiceInputEnabled =
    useFeatureFlag("voice-input-message-bar") === true;
  const messageInputTextValue = useUIStore(
    (state) => state.messageInputTextValue,
  );
  const setMessageInputText = useUIStore((state) => state.setMessageInputText);
  const setMessageInputTextValue = useUIStore(
    (state) => state.setMessageInputTextValue,
  );
  const setMessageInputFile = useUIStore((state) => state.setMessageInputFile);
  const setMessageForm = useUIStore((state) => state.setMessageForm);
  const confirmDialog = useUIStore((state) => state.confirmDialog);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const formRef = useSetElementOnStore<HTMLFormElement>(setMessageForm);
  const inputRef =
    useSetElementOnStore<HTMLTextAreaElement>(setMessageInputText);
  const inputFileRef =
    useSetElementOnStore<HTMLInputElement>(setMessageInputFile);
  const [isCompressing, setIsCompressing] = useState(false);
  const {
    mirrorRef,
    referenceRef,
    isMultiline,
    onChange: handleTextareaChange,
  } = useAutosizeTextarea({
    value: messageInputTextValue,
    onValueChange: setMessageInputTextValue,
  });
  const isDirty = !!messageInputTextValue.trim();

  const isMobile = useBreakpoint("mobile");
  const toastPosition: ToastPosition = isMobile
    ? "bottom-center"
    : "bottom-right";

  const pendingVoiceIntentRef = useRef<"append" | "send">("append");

  const {
    isSupported: isSpeechSupported,
    isListening,
    isStarting: isSpeechStarting,
    isProcessing: isSpeechProcessing,
    canRecord: canStartRecording,
    start,
    stop,
    discard,
  } = useVoiceTranscription({
      onPermissionError: () =>
        toast.error(
          "Sidekick needs microphone access to listen. Allow the mic, or type instead.",
          { position: toastPosition },
        ),
      onUnsupported: () =>
        toast.error("This browser can't use voice input. Type instead.", {
          position: toastPosition,
        }),
      onEmptyResult: () =>
        toast.error("Didn't catch that. Try recording again.", {
          position: toastPosition,
        }),
      onTranscriptionError: () =>
        toast.error("Couldn't transcribe your recording. Try again.", {
          position: toastPosition,
        }),
      onClipTooLarge: () =>
        toast.error("That recording is too long to transcribe. Try a shorter one.", {
          position: toastPosition,
        }),
      onTranscriptReady: (transcript) => {
        const existing = getComposerText().trim();
        setMessageInputTextValue(
          existing ? `${existing} ${transcript}` : transcript,
        );
        if (pendingVoiceIntentRef.current === "send") {
          formRef.current?.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true }),
          );
        }
      },
    });

  const startRecording = () => {
    // Defaults every new recording to "append" so a stop the user didn't explicitly
    // trigger (e.g. hitting the max recording duration) shows the transcript in the
    // box instead of auto-sending whatever the last recording's intent happened to be.
    pendingVoiceIntentRef.current = "append";
    start();
  };

  const stopAndAppend = () => {
    pendingVoiceIntentRef.current = "append";
    stop();
  };

  const stopAndSend = () => {
    pendingVoiceIntentRef.current = "send";
    stop();
  };

  const showAsMultiline = isMultiline && !isListening;

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const message = getComposerText().trim();

    if (!message) return;

    let fileUrl = null;
    if (selectedFile) {
      setIsCompressing(true);
      const fileToUpload =
        getFileType(selectedFile) === "image"
          ? await compressImage(selectedFile, { preset: "md" })
          : selectedFile;
      setIsCompressing(false);
      fileUrl = await onAttachFile(fileToUpload);
    }

    const fileType = selectedFile ? getFileType(selectedFile) : null;

    const text = formatMessage(message, { fileUrl, fileType });

    const tempMessage = getNewTempUserMessage({ text, user });

    if (!currentThread) {
      setMessages(PENDING_THREAD_KEY, [tempMessage]);
    }

    const attachedFile = selectedFile;

    resetFields();

    try {
      const data = await onSendMessage({ message: text });
      if (currentThread) {
        addMessage(data.threadId, tempMessage);
      }
    } catch (error) {
      if (!currentThread) {
        setMessages(PENDING_THREAD_KEY, []);
      }
      if (!getComposerText().trim()) {
        setMessageInputTextValue(message);
      }
      if (attachedFile) {
        setSelectedFile(attachedFile);
      }
      throw error;
    }
  };

  const resetFields = () => {
    setMessageInputTextValue("");
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
    setSelectedFile(null);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text/plain");
    const cleaned = pasted
      .replace(/\r\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trimEnd();
    const textarea = e.currentTarget;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const current = getComposerText();
    const cursor = start + cleaned.length;
    setMessageInputTextValue(
      current.slice(0, start) + cleaned + current.slice(end),
    );
    requestAnimationFrame(() => {
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isSubmitBlocked) {
        formRef.current?.dispatchEvent(
          new Event("submit", { cancelable: true, bubbles: true }),
        );
      }
    }
  };

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!formatMimetypeToFriendlyName(file.type)) {
      if (inputFileRef.current) {
        inputFileRef.current.value = "";
      }
      await confirmDialog({
        title: "File type not supported",
        content: (
          <div>
            <Typography mb={1}>Check the allowed files list below:</Typography>
            <Typography variant="body2">
              {getAllowedFiles().join(", ")}.
            </Typography>
          </div>
        ),
        confirmButtonText: "Close",
        cancelButtonText: false,
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveAttachedFile = () => {
    setSelectedFile(null);
    if (inputFileRef.current) {
      inputFileRef.current.value = "";
    }
  };

  const isSubmitBlocked =
    isSendingMessage ||
    isUploadingFile ||
    isCompressing ||
    isListening ||
    isSpeechProcessing ||
    !!disabled ||
    !!isSubmitDisabled;
  const isInputDisabled = isUploadingFile || isSpeechProcessing || !!disabled;

  return (
    <Tooltip
      title={tooltip ? tooltip : ""}
      open={!!tooltip}
      placement="top-start"
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [46, 0],
            },
          },
        ],
      }}
    >
      <MessageBarForm
        onSubmit={handleSendMessage}
        ref={formRef}
        id="message-bar-form"
        className={
          !isReceivingMessageResponse && !isOverlayHidden
            ? "blocked-overlay-container"
            : undefined
        }
      >
        <MessageBarContainer
          $isMultiline={showAsMultiline}
          className={classes([
            isInputDisabled && "disabled",
            hasMoreAutoInteractions &&
              !isReceivingMessageResponse &&
              "highlight-container",
          ])}
          id="sidekick-message-bar-input"
        >
          <input
            type="file"
            name="file"
            id="messageFile"
            onChange={handleChangeFile}
            ref={inputFileRef}
            disabled={isSubmitBlocked}
            hidden
          />
          {isListening ? (
            <Tooltip title="Discard" placement="top">
              <DiscardButton
                type="button"
                $isListening
                onClick={discard}
                aria-label="Discard"
              >
                <DeleteOutlineIcon />
              </DiscardButton>
            </Tooltip>
          ) : (
            <AttachButton
              htmlFor="messageFile"
              className={isSubmitBlocked ? "disabled" : ""}
              id="sidekick-message-bar-attachment-button"
            >
              {isUploadingFile ? (
                <CircularProgress size={16} />
              ) : (
                <AttachFileIcon />
              )}
            </AttachButton>
          )}
          <MessageBarTextarea
            mirrorRef={mirrorRef}
            referenceRef={referenceRef}
            textareaRef={inputRef}
            value={messageInputTextValue}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={getPlaceholder({
              hasMessages: numberOfMessages > 0,
              hasMlsAccessModule,
              isMobile,
            })}
            disabled={isInputDisabled}
            isListening={isListening}
            isMultiline={showAsMultiline}
            showTypewriter={
              !isSendingMessage &&
              !isReceivingMessageResponse &&
              hasMoreAutoInteractions
            }
            typewriterText={
              !isSendingMessage &&
              !isReceivingMessageResponse &&
              threadAutoInteractionConfig &&
              hasMoreAutoInteractions
                ? threadAutoInteractionConfig.messages[nextMessageIndex].text
                : undefined
            }
            onFinishTyping={handleFinishAutoTyping}
          />
          <UtilityBar>
            {isListening ? (
              <RecordingIndicator onStop={stopAndAppend} onSend={stopAndSend} />
            ) : (
              <>
                {isSpeechSupported && isVoiceInputEnabled && (
                  <MicButton
                    type="button"
                    disabled={
                      isSubmitBlocked || isSpeechStarting || !canStartRecording
                    }
                    onClick={startRecording}
                    aria-label="Start recording"
                    id="sidekick-message-bar-mic-button"
                  >
                    {isSpeechStarting || isSpeechProcessing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <MicIcon />
                    )}
                  </MicButton>
                )}
                <SendButton
                  type="submit"
                  disabled={isSubmitBlocked || !isDirty}
                >
                  {isSendingMessage ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ArrowUpwardIcon />
                  )}
                </SendButton>
              </>
            )}
          </UtilityBar>

          {selectedFile && (
            <FileName>
              <span>{formatMimetypeToFriendlyName(selectedFile.type)}</span>
              <span>{selectedFile.name}</span>
              <IconButton
                onClick={handleRemoveAttachedFile}
                disabled={isSubmitBlocked}
                size="small"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </FileName>
          )}
        </MessageBarContainer>
      </MessageBarForm>
    </Tooltip>
  );
};

export const MessageBarSkeleton = () => {
  return (
    <MessageBar
      onSendMessage={async () => ({ threadId: "" })}
      onAttachFile={async (f) => f.name}
      isSendingMessage={false}
      isReceivingMessageResponse={false}
      isUploadingFile={false}
      disabled
      numberOfMessages={0}
      handleFinishAutoTyping={() => {}}
      hasMoreAutoInteractions={false}
      nextMessageIndex={0}
      isOverlayHidden={true}
    />
  );
};
